import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, FileCheck2, AlertTriangle, Send, Star, MessageSquare, ShieldCheck, ChevronDown, ChevronUp, MessagesSquare } from "lucide-react";
import { api, fmtError, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, fmtEUR, L } from "@/constants/labels";
import RatingModal from "@/components/RatingModal";
import ResponseModal from "@/components/ResponseModal";
import { MissionVersements } from "@/components/versements/ChaineFinanciere";
import { MissionChaine } from "@/components/MissionChaine";
import ChantierDatesPanel from "@/components/ChantierDatesPanel";

const STATUTS = ["proposee", "acceptee", "en_cours", "terminee", "annulee"];

const PV_LABEL = {
  en_attente: "PV en attente de validation",
  valide: "PV validé",
  refuse: "PV refusé",
};
const PV_BADGE = {
  en_attente: "badge-warn",
  valide: "badge-valid",
  refuse: "badge-danger",
};
const PV_ICON = {
  en_attente: Clock,
  valide: CheckCircle2,
  refuse: XCircle,
};

function PVCell({ mission, role, onChanged }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    description_travaux: "",
    date_execution: new Date().toISOString().slice(0, 10),
  });
  const [motif, setMotif] = useState("");

  const openModal = async () => {
    setOpen(true);
    if (mission.pv_id) {
      try {
        const { data } = await api.get(`/missions/${mission.id}/pv`);
        setDetail(data);
      } catch (err) {
        setDetail(null);
      }
    }
  };
  const close = () => { setOpen(false); setDetail(null); setForm({ description_travaux: "", date_execution: new Date().toISOString().slice(0, 10) }); setMotif(""); };

  const submitPV = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/missions/${mission.id}/pv`, form);
      toast.success("PV de réception soumis. En attente de validation.");
      close(); onChanged();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  const validate = async () => {
    if (!window.confirm("Valider ce PV ? Cela déclare formellement la réception des travaux et débloque l'émission de la facture.")) return;
    setBusy(true);
    try {
      await api.post(`/missions/${mission.id}/pv/valider`);
      toast.success("PV validé. La facture peut désormais être émise.");
      close(); onChanged();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  const refuse = async () => {
    if (!motif || motif.length < 5) return toast.error("Motif obligatoire (min 5 caractères)");
    setBusy(true);
    try {
      await api.post(`/missions/${mission.id}/pv/refuser`, { motif });
      toast.success(L.wording.missionsPvRefuseToast);
      close(); onChanged();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };

  const st = mission.pv_statut;
  const Icon = st ? PV_ICON[st] : FileCheck2;

  const canSubmit = role === "prestataire" && ["acceptee", "en_cours", "terminee"].includes(mission.statut) && (!st || st === "refuse") && detail?.source !== "versements";
  const canValidate = role === "entreprise" && st === "en_attente" && detail?.source !== "versements";

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {st ? (
          <span className={PV_BADGE[st]} data-testid={`pv-badge-${mission.id}`}>
            <Icon size={12} /> {PV_LABEL[st]}
          </span>
        ) : (
          <span className="badge-muted"><FileCheck2 size={12} /> Aucun PV</span>
        )}
        <button onClick={openModal} className="btn-ghost text-xs underline" data-testid={`pv-open-${mission.id}`}>
          {canSubmit ? (st === "refuse" ? "Resoumettre" : "Soumettre") : canValidate ? "Instruire" : "Voir"}
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-3 overflow-y-auto" data-testid={`pv-modal-${mission.id}`}>
          <div className="bg-white w-full max-w-2xl rounded-[6px] border border-[#E4E7EB] shadow-lg my-4">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E4E7EB] bg-[#F5F6F7]">
              <div className="flex items-center gap-2">
                <FileCheck2 size={18} className="text-[#1B3A5C]" />
                <h2 className="text-lg font-semibold text-[#1F2933]">PV de réception — {mission.titre}</h2>
              </div>
              <button onClick={close} className="btn-ghost text-sm" aria-label="Fermer">✕</button>
            </div>

            <div className="p-4 space-y-4 text-sm">
              {detail && (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className={PV_BADGE[detail.statut]}>{PV_LABEL[detail.statut]}</span>
                    <span className="text-xs text-[#3E4C59]">
                      Soumis le {fmtDate(detail.submitted_at)} · Exécuté le {fmtDate(detail.date_execution)}
                    </span>
                  </div>
                  <div>
                    <div className="label">Description des travaux réalisés</div>
                    <div className="p-2 border border-[#E4E7EB] rounded-[4px] bg-[#F5F6F7] whitespace-pre-wrap">{detail.description_travaux}</div>
                  </div>
                  {detail.source === "versements" && (
                    <div className="space-y-1 text-xs">
                      <div>Réception : <b>{{ sans_reserve: "sans réserve", avec_reserves: `avec ${detail.reserves?.length || 0} réserve(s)`, refus: "refusée" }[detail.type_reception]}</b>{detail.retenue_garantie?.active && <> · retenue de garantie {fmtEUR(detail.retenue_garantie.montant_ht)} HT</>}</div>
                      {(detail.reserves || []).map((r, i) => <div key={r.id} className="p-2 bg-[#F5F6F7] rounded">{i + 1}. {r.description} — <i>{{ ouverte: "ouverte", levee_declaree: "levée déclarée", levee: "levée" }[r.statut]}</i></div>)}
                      <a href={`${BACKEND_URL}/api/missions/${mission.id}/pv/pdf`} target="_blank" rel="noreferrer" className="btn-outline text-xs inline-flex" data-testid={`pv-pdf-${mission.id}`}><FileCheck2 size={12} /> PV signé (PDF)</a>
                    </div>
                  )}
                  {detail.statut === "refuse" && detail.motif_refus && (
                    <div className="border-l-4 border-[#B7443A] bg-[#FEF2F2] p-3 text-[13px]">
                      <p className="font-semibold text-[#1F2933] mb-1 flex items-center gap-1"><AlertTriangle size={14} /> Motif du refus</p>
                      <p>{detail.motif_refus}</p>
                    </div>
                  )}
                  {detail.statut === "valide" && detail.validated_at && (
                    <div className="text-xs text-[#3E4C59]">Validé le {fmtDate(detail.validated_at)} — la facture peut être émise.</div>
                  )}
                  {(detail.history || []).length > 0 && (
                    <details className="text-xs text-[#3E4C59]">
                      <summary className="cursor-pointer">Historique ({detail.history.length} version(s) précédente(s))</summary>
                      <ul className="mt-1 space-y-1">
                        {detail.history.map((h, i) => (
                          <li key={i} className="p-2 bg-[#F5F6F7] rounded-[4px]">
                            {fmtDate(h.at)} — {h.statut}
                            {h.motif_refus && <div className="italic">Motif : {h.motif_refus}</div>}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              {canSubmit && (
                <form onSubmit={submitPV} className="border-t border-[#E4E7EB] pt-4 space-y-3" data-testid={`pv-form-${mission.id}`}>
                  <div className="text-sm font-semibold text-[#1F2933]">
                    {detail?.statut === "refuse" ? "Resoumettre après correction" : "Soumettre le PV de réception"}
                  </div>
                  <div>
                    <label className="label">Date de fin d'exécution des travaux</label>
                    <input
                      type="date"
                      required
                      className="input"
                      value={form.date_execution}
                      onChange={(e) => setForm({ ...form, date_execution: e.target.value })}
                      data-testid={`pv-date-${mission.id}`}
                    />
                  </div>
                  <div>
                    <label className="label">Description détaillée des travaux réalisés</label>
                    <textarea
                      required
                      minLength={5}
                      maxLength={5000}
                      rows={5}
                      className="textarea"
                      placeholder="Nature des prestations, avancement, points de vigilance, réserves éventuelles…"
                      value={form.description_travaux}
                      onChange={(e) => setForm({ ...form, description_travaux: e.target.value })}
                      data-testid={`pv-description-${mission.id}`}
                    />
                  </div>
                  <div className="text-xs text-[#3E4C59]">
                    En soumettant ce PV, vous déclarez l'achèvement des travaux au sens de l'article 1792-6 du Code civil.
                    L'émission de la facture sera possible dès validation par le donneur d'ordre.
                  </div>
                  <button type="submit" disabled={busy} className="btn-primary" data-testid={`pv-submit-${mission.id}`}>
                    <Send size={14} /> {busy ? "…" : "Soumettre le PV"}
                  </button>
                </form>
              )}

              {canValidate && (
                <div className="border-t border-[#E4E7EB] pt-4 space-y-3">
                  <div className="text-sm font-semibold text-[#1F2933]">Instruire ce PV</div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={validate} disabled={busy} className="btn-primary" data-testid={`pv-validate-${mission.id}`}>
                      <CheckCircle2 size={14} /> Valider — réception acceptée
                    </button>
                  </div>
                  <div className="pt-2 border-t border-[#E4E7EB]">
                    <label className="label">Motif de refus (obligatoire pour refuser)</label>
                    <textarea
                      rows={3}
                      className="textarea"
                      placeholder={L.wording.missionsReservesPlaceholder}
                      value={motif}
                      onChange={(e) => setMotif(e.target.value)}
                      minLength={5}
                      maxLength={2000}
                      data-testid={`pv-motif-${mission.id}`}
                    />
                    <button onClick={refuse} disabled={busy || motif.length < 5} className="btn-outline mt-2" data-testid={`pv-refuse-${mission.id}`}>
                      <XCircle size={14} /> Refuser avec réserves
                    </button>
                  </div>
                </div>
              )}

              {!detail && !canSubmit && !canValidate && (
                <p className="text-sm text-[#3E4C59]">Aucun PV soumis pour cette mission.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function MesMissions() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [pendingIds, setPendingIds] = useState(new Set());
  const [receivedByMission, setReceivedByMission] = useState({});
  const [ratingMission, setRatingMission] = useState(null);
  const [responseAvis, setResponseAvis] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const toggleExpand = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const load = useCallback(async () => {
    const [{ data: missions }, { data: pending }, { data: received }] = await Promise.all([
      api.get("/missions"),
      api.get("/avis/pending").catch(() => ({ data: [] })),
      api.get("/avis/me").catch(() => ({ data: [] })),
    ]);
    setRows(missions);
    setPendingIds(new Set(pending.map((m) => m.id)));
    // Un avis par mission_id (reçu par l'user connecté) — pour proposer le
    // bouton "Répondre" en face de chaque mission concernée.
    const map = {};
    for (const a of received) {
      map[a.mission_id] = a;
    }
    setReceivedByMission(map);
  }, []);
  useEffect(() => { load(); }, [load]);

  const change = async (id, statut) => {
    try { await api.patch(`/missions/${id}/statut`, null, { params: { statut } }); toast.success("Statut mis à jour"); load(); }
    catch (err) { toast.error(fmtError(err)); }
  };

  const rate = async (mission_id) => {
    // Ouvre la modale de notation. Handler onglet-friendly, sans prompt() natif.
    const m = rows.find((r) => r.id === mission_id);
    setRatingMission(m || null);
  };

  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">Missions</h1>
      <div className="card mt-4 overflow-x-auto">
        <table className="table-compact w-full">
          <thead>
            <tr>
              <th></th>
              <th>Titre</th>
              <th>Contrepartie</th>
              <th>Montant HT</th>
              <th>Début</th>
              <th>Statut</th>
              <th>PV de réception</th>
              <th>Avis</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="text-center text-[#3E4C59]">{L.common.none}</td></tr>}
            {rows.map(m => {
              const canRate = m.statut === "terminee" && pendingIds.has(m.id);
              const receivedForMe = receivedByMission[m.id];
              const isOpen = expanded.has(m.id);
              return (
                <React.Fragment key={m.id}>
                <tr>
                  <td className="w-8">
                    <button
                      onClick={() => toggleExpand(m.id)}
                      className="btn-ghost text-xs"
                      title="Voir les dates et la chaîne financière"
                      aria-label="Voir la chaîne financière"
                      data-testid={`mission-toggle-jalons-${m.id}`}
                    >
                      {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  </td>
                  <td className="font-semibold">{m.titre}</td>
                  <td>{user.role === "entreprise" ? m.prestataire_nom : m.entreprise_nom}</td>
                  <td>{fmtEUR(m.montant_ht)}</td>
                  <td>{fmtDate(m.date_debut)}</td>
                  <td>
                    <select className="select" value={m.statut} onChange={(e) => change(m.id, e.target.value)} data-testid={`mission-statut-${m.id}`}>
                      {STATUTS.map(s => <option key={s} value={s}>{L.missionStatus[s]}</option>)}
                    </select>
                  </td>
                  <td><PVCell mission={m} role={user.role} onChanged={load} /></td>
                  <td className="space-y-1">
                    <Link
                      to={`/app/missions/${m.id}/messages`}
                      className="btn-outline text-xs inline-flex items-center gap-1"
                      data-testid={`mission-chat-${m.id}`}
                    ><MessagesSquare size={12} /> Messagerie chantier</Link>
                    {m.statut !== "annulee" && <MissionChaine mission={m} role={user.role} />}
                    {canRate && (
                      <button
                        onClick={() => rate(m.id)}
                        className="btn-accent text-xs flex items-center gap-1"
                        data-testid={`mission-rate-${m.id}`}
                      ><Star size={12} /> Noter</button>
                    )}
                    {!canRate && m.statut === "terminee" && !pendingIds.has(m.id) && (
                      <span className="badge-valid text-xs"><CheckCircle2 size={10} /> Noté</span>
                    )}
                    {receivedForMe && !receivedForMe.reponse && (
                      <button
                        onClick={() => setResponseAvis(receivedForMe)}
                        className="btn-outline text-xs flex items-center gap-1"
                        data-testid={`mission-reply-${m.id}`}
                      ><MessageSquare size={12} /> Répondre à l'avis reçu</button>
                    )}
                    {receivedForMe?.reponse && (
                      <button
                        onClick={() => setResponseAvis(receivedForMe)}
                        className="btn-ghost text-xs flex items-center gap-1 text-[#3E4C59]"
                        data-testid={`mission-edit-reply-${m.id}`}
                      ><MessageSquare size={12} /> Réponse publiée · modifier</button>
                    )}
                  </td>
                </tr>
                {isOpen && (
                  <tr data-testid={`mission-jalons-row-${m.id}`}>
                    <td colSpan={8} className="bg-[#F5F6F7] border-t border-[#E4E7EB] p-3">
                      <div className="space-y-3">
                        <ChantierDatesPanel mission={m} role={user.role} onChanged={load} />
                        <MissionVersements mission={m} role={user.role} />
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {ratingMission && (
        <RatingModal
          mission={ratingMission}
          contrepartie={user.role === "entreprise" ? ratingMission.prestataire_nom : ratingMission.entreprise_nom}
          onClose={() => setRatingMission(null)}
          onSubmitted={load}
        />
      )}
      {responseAvis && (
        <ResponseModal
          avis={responseAvis}
          onClose={() => setResponseAvis(null)}
          onSubmitted={load}
        />
      )}
    </div>
  );
}
